import type { TxSummary } from '@apm/contracts';
import { statusLevel } from '@apm/core';

/**
 * 인스턴스별 최근 오류 수 (16회차).
 *
 * 서버의 `/api/external/status`가 같은 값을 내지만 콘솔은 그것을 부르지 않는다. 그쪽은 밖에
 * 내놓는 자리(토큰이 필요하다)이고, 콘솔은 이미 `/api/stream`으로 끝난 요청을 다 받고 있어
 * 여기서 세면 요청이 하나도 늘지 않는다. 판정 기준(`statusLevel`)은 서버와 같다.
 *
 * sse는 빼고 센다 (2026-09-07 결정 4). 연결이 수십 분 유지돼 건수를 통째로 흔든다.
 */

export interface ErrorCounts {
  /** 5xx·예외로 끝난 요청 */
  errors: number;
  /** 4xx이거나 요청 안에서 SQL이 깨진 것 */
  fails: number;
}

/** 인스턴스 키. 구분자는 이름에 못 들어가는 NUL이다(스토어의 키와 같은 방식) */
export function instanceKey(service: string, instance?: string): string {
  return `${service}\u0000${instance ?? ''}`;
}

/**
 * `windowMs` 안에 끝난 요청만 센다. 기준은 행의 `startedAt + durationMs`다.
 * 스토어가 이미 5분 창으로 잘라 두지만, 카드에 적는 숫자는 그 창과 따로 정해야
 * "최근 5분"이라는 문구가 맞다.
 */
export function countErrors(
  rows: readonly TxSummary[],
  windowMs: number,
  now: number,
): Map<string, ErrorCounts> {
  const cutoff = now - windowMs;
  const out = new Map<string, ErrorCounts>();
  for (const row of rows) {
    if (row.txType === 'sse') continue;
    if (row.startedAt + row.durationMs < cutoff) continue;
    const level = statusLevel(row);
    if (level === 'ok') continue;
    const key = instanceKey(row.service, row.instance);
    const bucket = out.get(key) ?? { errors: 0, fails: 0 };
    if (level === 'error') bucket.errors += 1;
    else bucket.fails += 1;
    out.set(key, bucket);
  }
  return out;
}
